<template>
	<div class="content-page content-page--customer-forms">
		<ActionSetNav
			:name="pageTitle"
			:display="true"
			:actions="mainNavActions"
			ref="customerNav"
		/>
		<div class="content-page__heading" ref="contentPageHeading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title" data-test="customerTitle">
					Forms
					<span class="content-page__title--detail" v-if="pageTitle"
						>- {{ pageTitle }}
					</span>
				</h2>
			</div>
			<div class="content-page__actions">
				<MenuButton
					variantName="primary"
					:disabled="!hasCreatePermission"
					:items="formCreateOptions"
					:placeholder="'Add Form'"
					v-model="formCreateValue"
					@change="addForm"
				/>
			</div>
		</div>
		<CustomerFormsManagerFilters
			v-model="filters"
			:defaultFilters="defaultFilters"
			:counter="resultCounter"
		/>
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<CustomerFormsList :items="customerForms.items" />
		</ScrollContainer>
		<Spinner
			v-if="loading"
			:processor="{ loading: true }"
			data-test="spinner"
		/>
		<NoForms v-if="!this.loading && noFormsAssigned" :title="noFormTitle" />
		<NoResults
			v-if="!this.loading && !hasResults && !noFormsAssigned"
			data-test="noResults"
		/>
		<CopyFormAddModal
			:actionType="actionType"
			:items="customerForms.items"
			v-model="showModal"
			@toast="updateToast"
			data-test="copyFormAddModal"
		/>
	</div>
</template>

<script>
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs.vue';
import CustomerFormsManagerFilters from './customer-forms-manager-filters';
import MenuButton from '@/components/forms/button/menu-button';
import NoForms from '@/views/customer-forms/no-forms';
import CopyFormAddModal from '@/views/global-forms/copy-form-from-modal';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import CustomerFormsList from '@/views/customer-forms/customer-forms-list';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';

// vuex
import { mapState, mapGetters, mapActions } from 'vuex';

// mixins
//;
import CustomerCardActions from '@/mixins/customers/customer-card-action';

// libraries
import _ from 'lodash';
import PageKeyNames from '@/utils/page-key-names';
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

export default {
	name: 'CustomerFormManager',
	mixins: [CustomerCardActions],
	components: {
		ActionSetNav,
		CustomerFormsManagerFilters,
		CopyFormAddModal,
		MenuButton,
		Breadcrumbs,
		NoForms,
		ScrollContainer,
		CustomerFormsList,
		Spinner,
		NoResults
	},
	props: {
		search: String,
		enabled: { type: Boolean, default: true },
		type: String
	},
	data() {
		return {
			page: 1,
			filters: {},
			defaultFilters: {
				search: '',
				enabled: true, // true, false, undefined
				type: 'all' // i, l, undefined
			},
			customer: undefined,
			formCreateValue: {
				text: 'Add Form',
				value: 'default'
			},
			formCreateOptions: [
				{
					text: 'Copy Of',
					value: 'c'
				},
				{
					text: 'Create New Laser',
					value: 'l'
				},
				{
					text: 'Create New Impact',
					value: 'i'
				},
				{
					text: 'Assign Existing Form',
					value: 'a'
				}
			],
			showModal: false,
			toastCounter: 0,
			toastClassName: '',
			toastMessage: '',
			actionType: '',
			loading: true,
			loadingMore: false,
			pageTitle: '',
			defaultUrlClientsFormsManager: [
				{
					url: `/clients`,
					name: 'Clients'
				},
				{
					name: 'Forms'
				}
			]
		};
	},
	created() {
		this.customer = this.customerById(this.customerId);
		this.mainNavActions = this.actions;
		this.setCustomerId(this.customerId); //DEV: check with Jorge, this customerId is used in copy-form-from-modal (...mapState('customerForms', ['customerId'])) but customerId is always null or undefined		this.mainNavActions = this.actions;
	},
	mounted() {
		this.$store.commit('setCurrentPage', PageKeyNames.FORM);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	computed: {
		...mapState('customerForms', ['customerForms', 'updatingStatus']),
		...mapGetters('customers', ['customerById']),
		addFormDisabled() {
			return this.validatePermission('forms.create');
		},
		hasResults() {
			return this.customerForms.items?.length > 0 && !this.loading;
		},
		resultCounter() {
			return `${this.customerForms.items?.length} of ${this.customerForms.totalCount}`;
		},
		noFormTitle() {
			if (!this.hasResults) {
				let enabledText = '';
				let typeText = '';
				if (this.filters.enabled === true) {
					enabledText = 'enabled';
				}
				if (this.filters.enabled === false) {
					enabledText = 'disabled';
				}
				if (this.filters.type === 'i') {
					typeText = 'impact';
				}
				if (this.filters.type === 'l') {
					typeText = 'laser';
				}
				return `No ${enabledText} ${typeText} forms have been assigned to this client.`;
			}

			return '';
		},
		hasCreatePermission() {
			return this.validatePermission('forms.create');
		},
		noFormsAssigned() {
			return (
				!this.hasResults &&
				this.filters.search === '' &&
				this.filters.enabled === 'all' &&
				this.filters.type === 'all' &&
				!this.customerForms.items?.length &&
				!this.updatingStatus
			);
		},
		hasMoreItemsToLoad() {
			return (
				this.customerForms.items?.length < this.customerForms.totalCount
			);
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);
			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}
			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlClientsFormsManager;
		}
	},
	methods: {
		...mapActions('customerForms', ['getFormsByCustomer', 'setCustomerId']),
		addForm(obj) {
			if (obj.value === 'l') {
				this.$router.push({
					path: `/clients/${this.$route.params.customerId}/forms/add/laser`
				});
			} else if (obj.value === 'i') {
				this.$router.push({
					path: `/clients/${this.$route.params.customerId}/forms/add/impact`
				});
			} else if (obj.value === 'c' || obj.value === 'a') {
				this.actionType =
					obj.value === 'c'
						? 'Copy'
						: obj.value === 'a'
						? 'Assign'
						: '';
				this.showModal = true;
			}
			this.formCreateValue = {
				text: 'Add Form',
				value: 'default'
			};
		},
		updateToast(val) {
			if (val.status === 200) {
				this.toastClassName = 'toast--success';
				this.toastMessage = val.message;
			} else {
				this.toastClassName = 'toast--error';
				this.toastMessage = val.message;
			}
			this.toastCounter++;
		},
		async fetchData() {
			if (!this.loadingMore) {
				this.loading = true;
			}

			let query = removeObjectEmptyValues({
				...this.filters,
				id: this.customerId
			});

			query.page = this.page;
			query.count = 100;

			const valuesToRemove = [undefined, '', 'all'];
			query = _.omitBy(query, value => valuesToRemove.includes(value));
			try {
				await this.getFormsByCustomer(query);
				if (!this.pageTitle) {
					this.pageTitle = this.customerForms.pageTitle;
				}
			} catch (error) {
				console.log(error);
			} finally {
				this.loading = false;
				this.loadingMore = false;
			}
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);
			queries = removeObjectEmptyValues(queries);

			this.$router
				.push({
					path: `/clients/${this.customerId}/forms`,
					query: queries
				})
				.catch(() => {});
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;

				this.page++;
				this.fetchData();
			}
		}
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.page = 1;
				this.updateRoute();
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				this.page = 1;

				if (
					newQuery.enabled === 'true' ||
					newQuery.enabled === 'false'
				) {
					newQuery.enabled = JSON.parse(newQuery.enabled);
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchData();
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		const routePages = [
			'customer-form-history',
			'impact-form-edit-designer',
			'impact-form-preview-designer',
			'laser-form-edit-designer',
			'laser-form-preview-designer'
		];
		const breadcrumbMapping = {
			'impact-form-edit-designer': 'Form Designer',
			'impact-form-preview-designer': 'Form Details',
			'laser-form-edit-designer': 'Form Designer',
			'laser-form-preview-designer': 'Form Details',
			'customer-form-history': 'History'
		};
		if (routePages.includes(to.name)) {
			let actualBreadcrumb =
				from.meta && from.meta.breadcrumb
					? [...from.meta.breadcrumb]
					: [];

			if (actualBreadcrumb.length === 0) {
				actualBreadcrumb = [...this.defaultUrlClientsFormsManager];
			}

			const getBreadcrumbName = key => breadcrumbMapping[key] || key;

			const breadcrumbName = getBreadcrumbName(to.name);

			const formsPageIndex = actualBreadcrumb.findIndex(
				obj => obj.name === 'Forms' && !obj.url
			);

			if (formsPageIndex !== -1) {
				actualBreadcrumb[formsPageIndex].url = from.fullPath;
			}

			actualBreadcrumb = [...actualBreadcrumb, { name: breadcrumbName }];

			to.meta.breadcrumb = actualBreadcrumb;

			const obj = { path: to.fullPath, breadcrumb: actualBreadcrumb };
			this.$store.commit('setBreadCrumbs', obj);
		}
		next();
	}
};
</script>

<style lang="scss" scoped>
/**
  * [Description]
  *
  * Global Forms Manager Header
  * Can use SCSS variables = TRUE
  *
  *
**/

::v-deep .results-toolbar .results-toolbar__search {
	@include mq($from: md) {
		width: 310px;
	}
}

::v-deep .content-page__actions .dropdown__list-container {
	width: 160px;
	left: initial;
	right: 0;
}

.content-page {
	&__heading {
		@include mq($until: sm-l) {
			flex-direction: column;
			align-items: unset;
		}
	}
	&__actions {
		@include mq($until: md) {
			margin-top: $gutter-md;
		}
	}
}
</style>
